import { GridColDef } from '@mui/x-data-grid-pro';
import { toNum } from './utils';

// A general filter over any column the table is showing, replacing the fixed set
// of min-spend / min-ROAS style inputs. Rules live in the URL so a filtered view
// can be sent to someone else, and anything that doesn't parse is dropped
// silently — a stale or hand-edited link should open the dashboard, not an error.

export type FilterFieldType = 'number' | 'date' | 'text';

export interface FilterRule {
  field: string;
  operator: string;
  value: string;
}

export interface FilterField {
  field: string;
  label: string;
  type: FilterFieldType;
}

// Columns rendered as dates. Everything else is classified from the column's own
// grid type, so a new column becomes filterable without touching this file.
const DATE_FIELDS = new Set(['created_time', 'first_seen', 'last_seen']);

// Identity columns hold a mix of things and are already covered by the search
// box, so they aren't offered as filter fields.
const EXCLUDED_FIELDS = new Set(['group_key']);

export const OPERATORS: Record<FilterFieldType, { value: string; label: string }[]> = {
  number: [
    { value: 'gte', label: '≥' },
    { value: 'lte', label: '≤' },
    { value: 'gt', label: '>' },
    { value: 'lt', label: '<' },
    { value: 'eq', label: '=' },
  ],
  date: [
    { value: 'gte', label: 'on or after' },
    { value: 'lte', label: 'on or before' },
  ],
  text: [
    { value: 'contains', label: 'contains' },
    { value: 'ncontains', label: 'does not contain' },
    { value: 'eq', label: 'is' },
  ],
};

export function fieldsFromColumns(columns: GridColDef[]): FilterField[] {
  return columns
    .filter(column => !EXCLUDED_FIELDS.has(column.field))
    .map(column => ({
      field: column.field,
      label: column.headerName || column.field,
      type: DATE_FIELDS.has(column.field)
        ? ('date' as const)
        : column.type === 'number'
        ? ('number' as const)
        : ('text' as const),
    }));
}

function isIsoDate(value: string) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

// A rule survives only if the field still exists at this level, the operator is
// valid for that field's type, and the value parses. Anything else is dropped.
export function isValidRule(rule: FilterRule, fields: FilterField[]): boolean {
  const field = fields.find(item => item.field === rule.field);
  if (!field) return false;
  if (!OPERATORS[field.type].some(op => op.value === rule.operator)) return false;
  if (!rule.value) return false;
  if (field.type === 'number' && !Number.isFinite(Number(rule.value))) return false;
  if (field.type === 'date' && !isIsoDate(rule.value)) return false;
  return true;
}

const RULE_SEPARATOR = '~';

export function serializeRules(rules: FilterRule[]): string {
  return rules
    .map(rule => `${rule.field}:${rule.operator}:${encodeURIComponent(rule.value)}`)
    .join(RULE_SEPARATOR);
}

// Parsing never throws: a malformed segment is skipped, not fatal.
export function parseRules(raw: string | null): FilterRule[] {
  if (!raw) return [];
  return raw
    .split(RULE_SEPARATOR)
    .map(part => {
      const first = part.indexOf(':');
      const second = part.indexOf(':', first + 1);
      if (first < 1 || second < 0) return null;
      let value = part.slice(second + 1);
      try {
        value = decodeURIComponent(value);
      } catch {
        // Leave the raw value; validation will drop it if it's unusable.
      }
      return {
        field: part.slice(0, first),
        operator: part.slice(first + 1, second),
        value,
      };
    })
    .filter((rule): rule is FilterRule => !!rule);
}

function compareNumber(actual: unknown, operator: string, target: number): boolean {
  // Coerced, not type-checked: a measure Snowflake returned as a string still
  // has to satisfy a numeric threshold. A row with no value can't satisfy one.
  const value = toNum(actual);
  if (value === null) return false;
  switch (operator) {
    case 'gte':
      return value >= target;
    case 'lte':
      return value <= target;
    case 'gt':
      return value > target;
    case 'lt':
      return value < target;
    case 'eq':
      return value === target;
    default:
      return true;
  }
}

function compareDate(actual: unknown, operator: string, target: string): boolean {
  if (!actual) return false;
  const day = String(actual).slice(0, 10);
  return operator === 'gte' ? day >= target : day <= target;
}

function compareText(actual: unknown, operator: string, target: string): boolean {
  const haystack = String(actual ?? '').toLowerCase();
  const needle = target.toLowerCase();
  switch (operator) {
    case 'contains':
      return haystack.includes(needle);
    case 'ncontains':
      return !haystack.includes(needle);
    case 'eq':
      return haystack === needle;
    default:
      return true;
  }
}

export function matchesRules(
  row: Record<string, unknown>,
  rules: FilterRule[],
  fields: FilterField[],
): boolean {
  return rules.every(rule => {
    const field = fields.find(item => item.field === rule.field);
    // A rule whose column isn't present at this level is ignored rather than
    // silently excluding everything.
    if (!field) return true;
    const actual = row[rule.field];
    if (field.type === 'number') {
      return compareNumber(actual, rule.operator, Number(rule.value));
    }
    if (field.type === 'date') return compareDate(actual, rule.operator, rule.value);
    return compareText(actual, rule.operator, rule.value);
  });
}

export function describeRule(rule: FilterRule, fields: FilterField[]): string {
  const field = fields.find(item => item.field === rule.field);
  const label = field?.label || rule.field;
  const operator =
    OPERATORS[field?.type || 'text'].find(op => op.value === rule.operator)?.label ||
    rule.operator;
  return `${label} ${operator} ${rule.value}`;
}
